import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/employee-redux";
import { Button, Divider, Form, Modal } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { hrrcUseOnlyCaseStatusOptions, SecondaryCaseStatus } from "@teamprevent/common/workAdjustment/models";

import { OhCase } from "../../../model";
import { CurrentUser } from "../../../../../auth";
import { ApplicationState } from "../../../../../applicationState";
import { changeWorkAdjustmentCaseSecondaryStatus } from "../../../actions";

import { WorkAdjustmentCaseAuthorizer } from "./WorkAdjustmentCaseAuthorizer";
import { ChangeWorkAdjustmentCaseSecondaryStatusModel, getPreviousNotes } from "./models";

interface OwnProps {
    workAdjustmentCase: OhCase;
}

const HrrcUseOnlyButton: React.FunctionComponent<OwnProps> = (props: OwnProps) => {
    const dispatch = useDispatch();

    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [secondaryStatus, setSecondaryStatus] = React.useState<keyof typeof SecondaryCaseStatus>(props.workAdjustmentCase.secondaryCaseStatus);
    const [newNotes, setNewNotes] = React.useState<string>(null);
    const previousNotes = getPreviousNotes(props.workAdjustmentCase.secondaryCaseStatusChangesNotes);

    const currentUser: CurrentUser = useSelector((state: ApplicationState) => state.currentUser);

    React.useEffect(() => {
        return resetState;
    }, []);

    const onHrrcUseOnlyButtonClick = () => {
        setModalOpen(true);
    };

    const onSecondaryStatusChange = (newStatus: keyof typeof SecondaryCaseStatus) => {
        setSecondaryStatus(newStatus);
    };

    const onNewNotesChange = (notes: string) => {
        setNewNotes(notes);
    };

    const onRebookCancel = () => resetState();

    const resetState = () => {
        setModalOpen(false);
        setSubmitted(false);
        setSecondaryStatus(props.workAdjustmentCase.secondaryCaseStatus);
        setNewNotes(null);
    };

    const markAsViewed = (): void => {
        setSubmitted(true);
        if (!secondaryStatus || !newNotes) {
            return;
        }

        setModalOpen(false);

        const model: ChangeWorkAdjustmentCaseSecondaryStatusModel = { secondaryStatus: SecondaryCaseStatus[secondaryStatus], newNotes };
        dispatch(changeWorkAdjustmentCaseSecondaryStatus(props.workAdjustmentCase.id, model));
        resetState();
    };

    if (!WorkAdjustmentCaseAuthorizer.isHrManager(currentUser)) {
        return null;
    }

    return (
        <>
            <Modal open={modalOpen}>
                <Modal.Header content="Case Status Notes" />
                <Modal.Content>
                    <Form>
                        <Input.Dropdown
                            required
                            value={secondaryStatus}
                            showErrors={submitted}
                            label="Status"
                            options={hrrcUseOnlyCaseStatusOptions}
                            onChange={onSecondaryStatusChange}
                        />
                        <Divider hidden />
                        <Input.Textarea
                            readOnly={true}
                            value={previousNotes}
                            label="Previous notes"
                        />
                        <Divider hidden />
                        <Input.Textarea
                            required
                            value={newNotes}
                            showErrors={submitted}
                            label="New notes"
                            onChange={onNewNotesChange}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={onRebookCancel} />
                    <ConfirmedButton
                        content="Confirm"
                        promptText={`Are you sure you want to mark this Work Adjustment case as ${secondaryStatus}?`}
                        onClick={markAsViewed}
                    />
                </Modal.Actions>
            </Modal>
            <Button icon="checkmark" content="HRRC Use Only" onClick={onHrrcUseOnlyButtonClick} />
        </>
    );
};

export { HrrcUseOnlyButton };
